import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowDownward,
  ArrowForward,
  ArrowUpward,
  Clear,
  History,
  MoreVert,
  Refresh,
  Search,
  UnfoldMore,
} from '@mui/icons-material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AppDropdown from '../../common/app-dropdown';
import ButtonTwo from '../../common/dashboard-button';
import ButtonOne from '../../common/normal-button';
import { ColorsConstants } from '../../cssUtils/constants';
import { useAuth } from '../../hooks/AuthProvider';
import { CTWA_LIST_CAMPAIGNS, CTWA_DRAFTS, CTWA_DRAFT_DETAIL, CTWA_UPDATE_CAMPAIGN_STATUS, CTWA_GET_AD_ACCOUNTS } from '../../config/urls';
import CustomPagination from '../../common/custom-pagination';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const OBJECTIVE_LABELS = {
  OUTCOME_ENGAGEMENT:    'Engagement',
  OUTCOME_TRAFFIC:       'Traffic',
  OUTCOME_LEADS:         'Leads',
  OUTCOME_SALES:         'Sales',
  OUTCOME_AWARENESS:     'Awareness',
  OUTCOME_APP_PROMOTION: 'App Promotion',
};

const BID_STRATEGY_LABELS = {
  LOWEST_COST_WITHOUT_CAP:  'Lowest Cost',
  LOWEST_COST_WITH_BID_CAP: 'Bid Cap',
  COST_CAP:                 'Cost Cap',
  LOWEST_COST_WITH_MIN_ROAS:'Min ROAS',
};

// Map Meta effective_status → BotBridge delivery label + colour
const DELIVERY_CONFIG = {
  ACTIVE:           { label: 'Active',       bg: '#DCFCE7', color: '#16A34A' },
  PAUSED:           { label: 'Paused',       bg: '#FEF9C3', color: '#CA8A04' },
  CAMPAIGN_PAUSED:  { label: 'Campaign Off', bg: '#FEF9C3', color: '#CA8A04' },
  ADSET_PAUSED:     { label: 'AdSet Off',    bg: '#FEF9C3', color: '#CA8A04' },
  DISAPPROVED:      { label: 'Disapproved',  bg: '#FEE2E2', color: '#DC2626' },
  PENDING_REVIEW:   { label: 'In Review',    bg: '#EDE9FE', color: '#7C3AED' },
  ARCHIVED:         { label: 'Archived',     bg: '#F3F4F6', color: '#6B7280' },
  DELETED:          { label: 'Deleted',      bg: '#FEE2E2', color: '#DC2626' },
  WITH_ISSUES:      { label: 'Issues',       bg: '#FEE2E2', color: '#DC2626' },
  COMPLETED:        { label: 'Completed',    bg: '#DBEAFE', color: '#2563EB' },
};

// Derive a BotBridge status from a Meta campaign object
const deriveBBStatus = (c) => {
  if (c.status === 'ARCHIVED') return 'Archived';
  if (c.status === 'DELETED')  return 'Deleted';
  const stop = c.stop_time ? new Date(c.stop_time) : null;
  if (stop && stop < new Date()) return 'Completed';
  if (c.effective_status === 'ACTIVE') return 'Active';
  if (c.effective_status === 'PAUSED') return 'Paused';
  return c.effective_status || c.status;
};

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────

const DeliveryChip = ({ campaign }) => {
  const key = campaign.effective_status || campaign.status;
  const cfg = DELIVERY_CONFIG[key] || { label: key, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <Chip label={cfg.label} size="small"
      sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
  );
};

const StatusChip = ({ campaign }) => {
  const label = deriveBBStatus(campaign);
  const cfgMap = {
    Active: { bg: '#DCFCE7', color: '#16A34A' },
    Paused: { bg: '#FEF9C3', color: '#CA8A04' },
    Completed: { bg: '#DBEAFE', color: '#2563EB' },
    Archived: { bg: '#F3F4F6', color: '#6B7280' },
    Deleted:  { bg: '#FEE2E2', color: '#DC2626' },
  };
  const cfg = cfgMap[label] || { bg: '#F3F4F6', color: '#6B7280' };
  return (
    <Chip label={label} size="small"
      sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
  );
};

const fmt = (n, digits = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: digits });

const fmtCurrency = (n, currency = 'INR') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency, maximumFractionDigits: 0,
  }).format(Number(n));
};

const fmtBudget = (c) => {
  if (c.daily_budget)    return `${fmtCurrency(Number(c.daily_budget) / 100)} / day`;
  if (c.lifetime_budget) return `${fmtCurrency(Number(c.lifetime_budget) / 100)} lifetime`;
  return '—';
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  // Epoch-zero sentinel Meta uses for "no end date"
  if (d.getFullYear() < 1971) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Pull a single metric value from insights.actions array
const getAction = (insights, actionType) => {
  const actions = insights?.actions || [];
  const hit = actions.find((a) => a.action_type === actionType);
  return hit ? Number(hit.value) : null;
};

const getCostPerAction = (insights, actionType) => {
  const cpa = insights?.cost_per_action_type || [];
  const hit = cpa.find((a) => a.action_type === actionType);
  return hit ? Number(hit.value) : null;
};

// Primary result action for CTWA is onsite_conversion.messaging_conversation_started_7d
const PRIMARY_ACTION = 'onsite_conversion.messaging_conversation_started_7d';

const getSortValue = (c, field) => {
  const ins = c.insights?.data?.[0];
  switch (field) {
    case 'name':         return (c.name || '').toLowerCase();
    case 'created_time': return c.created_time || '';
    case 'budget':       return parseFloat(c.daily_budget || c.lifetime_budget || 0);
    case 'spend':        return parseFloat(ins?.spend || 0);
    case 'impressions':  return parseInt(ins?.impressions || 0, 10);
    case 'reach':        return parseInt(ins?.reach || 0, 10);
    default:             return '';
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Filter option lists and label → Meta API value maps
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS   = ['Active', 'Paused', 'Archived', 'Deleted'];
const DELIVERY_OPTIONS = ['Active', 'Paused', 'Disapproved', 'In Review', 'Issues', 'Completed', 'Archived'];
const OBJECTIVE_OPTIONS = ['Engagement', 'Traffic', 'Leads', 'Sales', 'Awareness', 'App Promotion'];
const BUDGET_OPTIONS   = ['Daily', 'Lifetime'];

const STATUS_TO_API    = { Active: 'ACTIVE', Paused: 'PAUSED', Archived: 'ARCHIVED', Deleted: 'DELETED' };
const DELIVERY_TO_API  = { Active: 'ACTIVE', Paused: 'PAUSED', Disapproved: 'DISAPPROVED', 'In Review': 'PENDING_REVIEW', Issues: 'WITH_ISSUES', Completed: 'COMPLETED', Archived: 'ARCHIVED' };
const OBJECTIVE_TO_API = { Engagement: 'OUTCOME_ENGAGEMENT', Traffic: 'OUTCOME_TRAFFIC', Leads: 'OUTCOME_LEADS', Sales: 'OUTCOME_SALES', Awareness: 'OUTCOME_AWARENESS', 'App Promotion': 'OUTCOME_APP_PROMOTION' };

// ─────────────────────────────────────────────────────────────────────────────
// Table header
// ─────────────────────────────────────────────────────────────────────────────

const TH = ({ children, align = 'left', minWidth, sortKey, sortBy, sortOrder, onSort }) => {
  const active = sortKey && sortBy === sortKey;
  return (
    <TableCell
      align={align}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      sx={{
        fontWeight: 700, fontSize: 11, color: ColorsConstants.primaryGray50,
        py: 1.5, whiteSpace: 'nowrap', minWidth,
        backgroundColor: ColorsConstants.primaryBlue60,
        cursor: sortKey ? 'pointer' : 'default',
        userSelect: 'none',
        '&:hover': sortKey ? { backgroundColor: ColorsConstants.primaryBlue60, opacity: 0.85 } : {},
      }}
    >
      {sortKey ? (
        <Stack direction="row" alignItems="center" gap={0.5} justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}>
          {children}
          {active
            ? sortOrder === 'asc'
              ? <ArrowUpward sx={{ fontSize: 14, color: '#fff' }} />
              : <ArrowDownward sx={{ fontSize: 14, color: '#fff' }} />
            : <UnfoldMore sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />}
        </Stack>
      ) : children}
    </TableCell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Row actions menu
// ─────────────────────────────────────────────────────────────────────────────

const ActionsMenu = ({ campaign, adAccountId }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}>
        <MenuItem dense onClick={() => {
          window.open(
            `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${adAccountId?.replace('act_', '')}&selected_campaign_ids=${campaign.id}`,
            '_blank'
          );
          setAnchor(null);
        }}>
          View in Ads Manager
        </MenuItem>
        <MenuItem dense onClick={() => { navigator.clipboard.writeText(campaign.id); setAnchor(null); }}>
          Copy Campaign ID
        </MenuItem>
      </Menu>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const CTWACampaignList = ({ onCreateNew, onContinueDraft, onBack, adAccountId, onViewCampaign, onViewAuditLog, canCreate = true, canPause = true }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [adAccounts, setAdAccounts] = useState([]);
  const [adAccountsLoading, setAdAccountsLoading] = useState(false);
  const [activeAdAccountId, setActiveAdAccountId] = useState(adAccountId || '');

  const [campaigns, setCampaigns] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [toggling, setToggling] = useState(new Set());
  const [sortBy,      setSortBy]      = useState('created_time');
  const [sortOrder,   setSortOrder]   = useState('desc');
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [objectiveFilter, setObjectiveFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [appliedDateRange, setAppliedDateRange] = useState([null, null]);
  const [dateError, setDateError] = useState(null);
  const [budgetType, setBudgetType] = useState('');
  const debounceRef = useRef(null);
  const mountedRef = useRef(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchCampaigns = useCallback(async ({
    search,
    status, delivery, objective, dateFrom, dateTo,
  } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant };
      if (!activeAdAccountId) { setCampaigns([]); setLoading(false); return; }

      const params = new URLSearchParams({ ad_account_id: activeAdAccountId });
      if (search)   params.set('search', search);
      if (status)   params.set('status',           STATUS_TO_API[status]    || status);
      if (delivery) params.set('effective_status',  DELIVERY_TO_API[delivery] || delivery);
      if (objective) params.set('objective',        OBJECTIVE_TO_API[objective] || objective);
      if (dateFrom) params.set('date_from', dayjs(dateFrom).format('YYYY-MM-DD'));
      if (dateTo)   params.set('date_to',   dayjs(dateTo).format('YYYY-MM-DD'));

      const res = await fetch(`${CTWA_LIST_CAMPAIGNS}?${params}`, { headers });
      if (res.ok) {
        const d = await res.json();
        setCampaigns(d.campaigns || []);
        setPage(0);
        setSelected(new Set());
      } else {
        const d = await res.json();
        setError(d.error || 'Failed to load campaigns from Meta.');
      }
    } catch {
      setError('Network error — could not load campaigns.');
    } finally {
      setLoading(false);
    }
  }, [activeAdAccountId, tenant, getValidToken]);

  // ── Debounced search ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchCampaigns({
        search: searchQuery.trim() || undefined,
        status: statusFilter, delivery: deliveryFilter, objective: objectiveFilter,
        dateFrom: fromDate, dateTo: toDate,
      });
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ad accounts for the selector ───────────────────────────────────

  useEffect(() => {
    (async () => {
      setAdAccountsLoading(true);
      try {
        const token = await getValidToken();
        const res = await fetch(CTWA_GET_AD_ACCOUNTS, {
          headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant },
        });
        if (res.ok) {
          const d = await res.json();
          setAdAccounts(d.ad_accounts || []);
        }
      } catch (_) {}
      finally { setAdAccountsLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter handlers ───────────────────────────────────────────────────────

  const handleStatusSelect = (value) => {
    const next = statusFilter === value ? '' : value;
    setStatusFilter(next);
    setPage(0);
    fetchCampaigns({ search: searchQuery.trim() || undefined, status: next, delivery: deliveryFilter, objective: objectiveFilter, dateFrom: fromDate, dateTo: toDate });
  };

  const handleDeliverySelect = (value) => {
    const next = deliveryFilter === value ? '' : value;
    setDeliveryFilter(next);
    setPage(0);
    fetchCampaigns({ search: searchQuery.trim() || undefined, status: statusFilter, delivery: next, objective: objectiveFilter, dateFrom: fromDate, dateTo: toDate });
  };

  const handleObjectiveSelect = (value) => {
    const next = objectiveFilter === value ? '' : value;
    setObjectiveFilter(next);
    setPage(0);
    fetchCampaigns({ search: searchQuery.trim() || undefined, status: statusFilter, delivery: deliveryFilter, objective: next, dateFrom: fromDate, dateTo: toDate });
  };

  const handleBudgetSelect = (value) => {
    const next = budgetType === value ? '' : value;
    setBudgetType(next);
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
    setPage(0);
    fetchCampaigns({ search: searchQuery.trim() || undefined, status: statusFilter, delivery: deliveryFilter, objective: objectiveFilter, dateFrom: appliedDateRange[0], dateTo: appliedDateRange[1] });
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([fromDate, toDate]);
    setDateError(null);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setDeliveryFilter('');
    setObjectiveFilter('');
    setFromDate(null);
    setToDate(null);
    setAppliedDateRange([null, null]);
    setDateError(null);
    setBudgetType('');
    setPage(0);
    fetchCampaigns();
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const hasActiveFilters = !!(searchQuery.trim() || statusFilter || deliveryFilter || objectiveFilter || fromDate || toDate || budgetType);

  const filteredCampaigns = budgetType === 'daily'
    ? campaigns.filter(c => c.daily_budget)
    : budgetType === 'lifetime'
    ? campaigns.filter(c => c.lifetime_budget)
    : campaigns;

  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    const va = getSortValue(a, sortBy);
    const vb = getSortValue(b, sortBy);
    if (va < vb) return sortOrder === 'asc' ? -1 : 1;
    if (va > vb) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const displayedCampaigns = sortedCampaigns.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const fetchData = useCallback(async () => {
    const token = await getValidToken();
    const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant };
    const [, draftRes] = await Promise.all([
      fetchCampaigns(),
      fetch(CTWA_DRAFTS, { headers }),
    ]);
    if (draftRes?.ok) {
      const d = await draftRes.json();
      setDrafts(d.drafts || []);
    }
  }, [fetchCampaigns, tenant, getValidToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const allIds = displayedCampaigns.map((c) => c.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // ── Campaign status toggle ─────────────────────────────────────────────────

  const handleToggleStatus = async (c) => {
    const newStatus = c.effective_status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setToggling((prev) => new Set(prev).add(c.id));
    try {
      const token = await getValidToken();
      const res = await fetch(CTWA_UPDATE_CAMPAIGN_STATUS(c.id), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Id': tenant,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus, campaign_name: c.name }),
      });
      if (res.ok) {
        setCampaigns((prev) =>
          prev.map((camp) =>
            camp.id === c.id
              ? { ...camp, status: newStatus, effective_status: newStatus }
              : camp
          )
        );
      } else {
        const d = await res.json();
        alert(d.error || 'Failed to update campaign status.');
      }
    } catch {
      alert('Network error — could not update campaign status.');
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
    }
  };

  // ── Draft delete ───────────────────────────────────────────────────────────

  const handleDeleteDraft = async (draftId) => {
    setDeletingDraftId(draftId);
    try {
      const token = await getValidToken();
      await fetch(CTWA_DRAFT_DETAIL(draftId), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant },
      });
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
    } finally {
      setDeletingDraftId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {onBack && (
            <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} color={ColorsConstants.primaryBlue100}>
              Campaigns
            </Typography>
            <Typography variant="body2" color={ColorsConstants.primaryGray50} sx={{ mt: 0.25 }}>
              Click-to-WhatsApp campaigns · this month's metrics
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => { handleResetFilters(); fetchData(); }} size="small" disabled={loading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Audit Logs">
            <IconButton onClick={onViewAuditLog} size="small">
              <History fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Show top-right CTA only when there are existing campaigns or drafts and user can create */}
          {canCreate && (campaigns.length > 0 || drafts.length > 0 || hasActiveFilters) && (
            <ButtonOne title="Create Campaign" startIcon={<Add fontSize="small" />} onClick={onCreateNew} />
          )}
        </Stack>
      </Stack>

      {/* Filter bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 3 },
        }}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by name or ID…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { height: 40, borderRadius: '8px', fontSize: 13 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: ColorsConstants.primaryGray40 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Date Range */}
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          paperProps={{ maxWidth: 460 }}
          menuContent={(closeMenu) => (
            <Box sx={{ p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2">Select date range</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <DatePicker
                      label="From"
                      value={appliedDateRange[0]}
                      onChange={(v) => {
                        setAppliedDateRange([v, appliedDateRange[1]]);
                        setDateError(appliedDateRange[1] && v && dayjs(v).isAfter(appliedDateRange[1]) ? 'From must be before To' : null);
                      }}
                      slotProps={{ textField: { size: 'small', sx: { width: 190, '& .MuiInputBase-root': { height: 40 } } } }}
                    />
                    <DatePicker
                      label="To"
                      value={appliedDateRange[1]}
                      onChange={(v) => {
                        setAppliedDateRange([appliedDateRange[0], v]);
                        setDateError(appliedDateRange[0] && v && dayjs(v).isBefore(appliedDateRange[0]) ? 'To must be after From' : null);
                      }}
                      slotProps={{ textField: { size: 'small', sx: { width: 190, '& .MuiInputBase-root': { height: 40 } } } }}
                    />
                  </Box>
                  {dateError && <Typography variant="caption" color="error">{dateError}</Typography>}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <ButtonTwo title="Cancel" onClick={() => { handleDateRangeCancel(); closeMenu(); }} />
                    <ButtonOne title="Apply" onClick={() => { handleDateRangeApply(); closeMenu(); }}
                      disabled={!appliedDateRange[0] || !appliedDateRange[1] || Boolean(dateError)} />
                  </Box>
                </Box>
              </LocalizationProvider>
            </Box>
          )}
        >
          Date Range
          {fromDate && toDate && (
            <Tooltip title={`${dayjs(fromDate).format('DD/MM/YYYY')} – ${dayjs(toDate).format('DD/MM/YYYY')}`} placement="top" arrow>
              <Chip
                label={`${dayjs(fromDate).format('DD/MM/YY')} – ${dayjs(toDate).format('DD/MM/YY')}`}
                size="small"
                onDelete={(e) => { e.stopPropagation(); setFromDate(null); setToDate(null); setAppliedDateRange([null, null]); fetchCampaigns({ search: searchQuery.trim() || undefined, status: statusFilter, delivery: deliveryFilter, objective: objectiveFilter }); }}
                sx={{ ml: 1, maxWidth: 150 }}
              />
            </Tooltip>
          )}
        </AppDropdown>

        {/* Campaign Status */}
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          menuItems={STATUS_OPTIONS.map(v => ({ label: v, value: v }))}
          selectedValue={statusFilter}
          onMenuSelect={handleStatusSelect}
        >
          Campaign Status
          {statusFilter && (
            <Chip label={statusFilter} size="small"
              onDelete={(e) => { e.stopPropagation(); handleStatusSelect(statusFilter); }}
              sx={{ ml: 1, maxWidth: 120 }} />
          )}
        </AppDropdown>

        {/* Delivery Status */}
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          menuItems={DELIVERY_OPTIONS.map(v => ({ label: v, value: v }))}
          selectedValue={deliveryFilter}
          onMenuSelect={handleDeliverySelect}
        >
          Delivery
          {deliveryFilter && (
            <Chip label={deliveryFilter} size="small"
              onDelete={(e) => { e.stopPropagation(); handleDeliverySelect(deliveryFilter); }}
              sx={{ ml: 1, maxWidth: 120 }} />
          )}
        </AppDropdown>

        {/* Campaign Objective */}
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          menuItems={OBJECTIVE_OPTIONS.map(v => ({ label: v, value: v }))}
          selectedValue={objectiveFilter}
          onMenuSelect={handleObjectiveSelect}
        >
          Objective
          {objectiveFilter && (
            <Chip label={objectiveFilter} size="small"
              onDelete={(e) => { e.stopPropagation(); handleObjectiveSelect(objectiveFilter); }}
              sx={{ ml: 1, maxWidth: 120 }} />
          )}
        </AppDropdown>

        {/* Budget Type (client-side) */}
        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          menuItems={BUDGET_OPTIONS.map(v => ({ label: v, value: v }))}
          selectedValue={budgetType === 'daily' ? 'Daily' : budgetType === 'lifetime' ? 'Lifetime' : ''}
          onMenuSelect={(v) => handleBudgetSelect(v === 'Daily' ? 'daily' : 'lifetime')}
        >
          Budget Type
          {budgetType && (
            <Chip label={budgetType === 'daily' ? 'Daily' : 'Lifetime'} size="small"
              onDelete={(e) => { e.stopPropagation(); setBudgetType(''); }}
              sx={{ ml: 1 }} />
          )}
        </AppDropdown>

        {/* Reset */}
        {hasActiveFilters && (
          <ButtonTwo
            startIcon={<Refresh />}
            onClick={handleResetFilters}
            sx={{
              height: 40,
              borderRadius: '8px',
              textTransform: 'capitalize',
              border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
              color: ColorsConstants.primaryBackgroundColor,
            }}
          >
            Reset
          </ButtonTwo>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color={ColorsConstants.primaryGray40} sx={{ mt: 2 }}>
            Loading all campaigns…
          </Typography>
        </Stack>
      )}

      {/* Error */}
      {!loading && error && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>
          <ButtonOne title="Retry" onClick={fetchData} />
        </Paper>
      )}

      {!loading && !error && (
        <>
          {/* ── Drafts ── */}
          {drafts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray60}>Drafts</Typography>
                <Chip label={drafts.length} size="small"
                  sx={{ height: 18, fontSize: 11, fontWeight: 700, backgroundColor: ColorsConstants.primaryGray10, color: ColorsConstants.primaryGray50 }} />
              </Stack>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH minWidth={200}>Campaign Name</TH>
                      <TH minWidth={110}>Last Saved</TH>
                      <TH align="right" minWidth={150}>Actions</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {drafts.map((draft) => (
                      <TableRow key={draft.id} hover onClick={() => onContinueDraft(draft.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Chip label="Draft" size="small"
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: '#FEF9C3', color: '#CA8A04', borderRadius: 1 }} />
                            <Typography variant="body2" fontWeight={600}>{draft.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={ColorsConstants.primaryGray50}>
                            {fmtDate(draft.updated_at) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" gap={1}>
                            {canCreate && (
                              <ButtonOne title="Continue" endIcon={<ArrowForward fontSize="small" />}
                                onClick={(e) => { e.stopPropagation(); onContinueDraft(draft.id); }}
                                sx={{ height: 30, fontSize: 12 }} />
                            )}
                            {canCreate && (
                              <ButtonOne title={deletingDraftId === draft.id ? '…' : 'Delete'} variant="outlined"
                                disabled={deletingDraftId === draft.id}
                                onClick={(e) => { e.stopPropagation(); handleDeleteDraft(draft.id); }}
                                sx={{ height: 30, fontSize: 12, color: '#EF4444', borderColor: '#FCA5A5', '&:hover': { backgroundColor: '#FEF2F2' } }} />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Published campaigns + Ad Account filter ── */}
          {campaigns.length > 0 && (
            <>
              {drafts.length > 0 && <Divider sx={{ mb: 3 }} />}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  {drafts.length > 0 && (
                    <>
                      <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray60}>Published</Typography>
                      <Chip label={filteredCampaigns.length} size="small"
                        sx={{ height: 18, fontSize: 11, fontWeight: 700, backgroundColor: ColorsConstants.primaryGray10, color: ColorsConstants.primaryGray50 }} />
                    </>
                  )}
                </Stack>
                <AppDropdown
                  endIcon={adAccountsLoading ? <CircularProgress size={12} /> : <KeyboardArrowDownOutlinedIcon />}
                  menuItems={adAccounts.map(acc => ({ label: acc.name, value: acc.id }))}
                  selectedValue={activeAdAccountId}
                  onMenuSelect={(id) => setActiveAdAccountId(id)}
                >
                  Ad Account
                  {activeAdAccountId && (
                    <Chip
                      label={adAccounts.find(a => a.id === activeAdAccountId)?.name || activeAdAccountId}
                      size="small"
                      sx={{ ml: 1, maxWidth: 140 }}
                    />
                  )}
                </AppDropdown>
              </Stack>
            </>
          )}

          {campaigns.length === 0 && drafts.length === 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 6, textAlign: 'center' }}>
              {hasActiveFilters ? (
                <>
                  <Typography variant="body1" fontWeight={700} color={ColorsConstants.primaryGray60} sx={{ mb: 0.5 }}>
                    No campaigns match your search or filters
                  </Typography>
                  <Typography variant="body2" color={ColorsConstants.primaryGray40}>
                    Try adjusting your search terms or clearing some filters.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight={700} color={ColorsConstants.primaryGray60} sx={{ mb: 0.5 }}>
                    No campaigns found.
                  </Typography>
                  <Typography variant="body2" color={ColorsConstants.primaryGray40} sx={{ mb: 3 }}>
                    Create your first Click-to-WhatsApp campaign to start getting leads.
                  </Typography>
                  {canCreate && <ButtonOne title="Create Campaign" startIcon={<Add />} onClick={onCreateNew} />}
                </>
              )}
            </Paper>
          )}

          {/* Budget type filter may hide all server-returned campaigns */}
          {campaigns.length > 0 && filteredCampaigns.length === 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color={ColorsConstants.primaryGray50}>
                No campaigns with a {budgetType} budget. <span style={{ cursor: 'pointer', color: ColorsConstants.primaryBlue100 }} onClick={() => { setBudgetType(''); setPage(0); }}>Clear budget filter</span>
              </Typography>
            </Paper>
          )}

          {filteredCampaigns.length > 0 && (
            <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: { sm: 480, md: 600 }, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    <TH minWidth={40}>
                      <Checkbox size="small" checked={allSelected} indeterminate={selected.size > 0 && !allSelected}
                        onChange={toggleAll}
                        sx={{ p: 0, color: ColorsConstants.primaryGray40, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } }} />
                    </TH>
                    <TH minWidth={50}>Off / On</TH>
                    <TH minWidth={220} sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Campaign Name</TH>
                    <TH minWidth={100}>Status</TH>
                    <TH minWidth={110}>Delivery</TH>
                    <TH align="right" minWidth={80}>Results</TH>
                    <TH align="right" minWidth={120}>Cost / Result</TH>
                    <TH align="right" minWidth={120} sortKey="budget" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Budget</TH>
                    <TH align="right" minWidth={110} sortKey="spend" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Amount Spent</TH>
                    <TH align="right" minWidth={100} sortKey="impressions" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Impressions</TH>
                    <TH align="right" minWidth={80} sortKey="reach" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Reach</TH>
                    <TH minWidth={160} sortKey="created_time" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Schedule</TH>
                    <TH minWidth={90}>Ends</TH>
                    <TH minWidth={100}>Bid Strategy</TH>
                    {/* Actions column hidden — BB-XXXX */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedCampaigns.map((c) => {
                    const ins = c.insights?.data?.[0];
                    const results = getAction(ins, PRIMARY_ACTION);
                    const cpr = getCostPerAction(ins, PRIMARY_ACTION);
                    const startDate = fmtDate(c.start_time);
                    const stopDate = fmtDate(c.stop_time);
                    const isActive = c.effective_status === 'ACTIVE';

                    return (
                      <TableRow key={c.id} hover selected={selected.has(c.id)}>

                        {/* Checkbox */}
                        <TableCell sx={{ py: 1 }}>
                          <Checkbox size="small" checked={selected.has(c.id)} onChange={() => toggleRow(c.id)}
                            sx={{ p: 0, color: ColorsConstants.primaryGray40, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } }} />
                        </TableCell>

                        {/* Off/On toggle */}
                        <TableCell sx={{ py: 1 }}>
                          <Tooltip title={!canPause ? 'You do not have permission to pause or resume campaigns' : isActive ? 'Pause campaign' : 'Activate campaign'}>
                            <span>
                              <Switch
                                size="small"
                                checked={isActive}
                                disabled={toggling.has(c.id) || !canPause}
                                onChange={() => canPause && handleToggleStatus(c)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': { color: canPause ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20 },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: canPause ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20 },
                                }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>

                        {/* Campaign name */}
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              lineHeight: 1.3,
                              cursor: 'pointer',
                              color: ColorsConstants.primaryBlue100,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => onViewCampaign?.(c.id)}
                          >
                            {c.name}
                          </Typography>
                          <Typography variant="caption" color={ColorsConstants.primaryGray40}>
                            {OBJECTIVE_LABELS[c.objective] || c.objective} · {c.id}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1 }}>
                          <StatusChip campaign={c} />
                        </TableCell>

                        {/* Delivery */}
                        <TableCell sx={{ py: 1 }}>
                          <DeliveryChip campaign={c} />
                        </TableCell>

                        {/* Results */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2">{results != null ? fmt(results) : '—'}</Typography>
                          {results != null && (
                            <Typography variant="caption" color={ColorsConstants.primaryGray40}>Conversations</Typography>
                          )}
                        </TableCell>

                        {/* Cost per Result */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2">{cpr != null ? fmtCurrency(cpr) : '—'}</Typography>
                        </TableCell>

                        {/* Budget */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{fmtBudget(c)}</Typography>
                        </TableCell>

                        {/* Amount spent */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2">{ins?.spend != null ? fmtCurrency(ins.spend) : '—'}</Typography>
                        </TableCell>

                        {/* Impressions */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2">{ins?.impressions != null ? fmt(ins.impressions) : '—'}</Typography>
                        </TableCell>

                        {/* Reach */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2">{ins?.reach != null ? fmt(ins.reach) : '—'}</Typography>
                        </TableCell>

                        {/* Schedule */}
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {startDate || '—'}
                            {stopDate ? ` → ${stopDate}` : ''}
                          </Typography>
                        </TableCell>

                        {/* Ends */}
                        <TableCell sx={{ py: 1 }}>
                          {stopDate ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{stopDate}</Typography>
                          ) : (
                            <Chip label="Ongoing" size="small"
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#16A34A', borderRadius: 1 }} />
                          )}
                        </TableCell>

                        {/* Bid Strategy */}
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" color={ColorsConstants.primaryGray60}>
                            {BID_STRATEGY_LABELS[c.bid_strategy] || c.bid_strategy || '—'}
                          </Typography>
                        </TableCell>

                        {/* Actions menu hidden — BB-XXXX */}

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Items-per-page selector — hide the right page-number nav */}
            {filteredCampaigns.length > 0 && (
              <Box sx={{ '& > div > div:last-child': { display: 'none' } }}>
                <CustomPagination
                  count={filteredCampaigns.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                />
              </Box>
            )}

            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CTWACampaignList;
